import { useQuery } from '@tanstack/react-query';
import { fetchAllPaginated } from '@/lib/supabase-paginate';
import type {
  DeviationProject,
  DeviationUser,
  EmployeeProjectHours,
} from '@/lib/budget-deviation-calc';
import type {
  ApprovedTaskAssignment,
  ApprovedTaskTimeEntry,
} from '@/lib/approved-task-deviation-calc';

interface RawProject {
  id: string;
  name: string;
  project_code: string | null;
  project_status: string;
  completed_at: string | null;
  customer: { name: string } | null;
}

interface RawBudgetLine {
  project_id: string;
  quantity_hours: number | null;
}

interface RawTimeEntry {
  project_id: string | null;
  user_id: string | null;
  budget_line_id: string | null;
  assignment_id: string | null;
  duration_seconds: number | null;
}

interface RawAssignment {
  id: string;
  user_id: string;
  budget_line_id: string;
  hours: number | null;
  label: string | null;
  task_status: string | null;
  completed_at: string | null;
  approved_at: string | null;
  budget_lines: {
    project_id: string;
    description: string;
  } | null;
}

interface RawUser {
  id: string;
  full_name: string;
  role: string;
}

export interface BudgetDeviationsData {
  /** Ferdigstilte prosjekter med team-basis solgt/brukt (Bonusmål-fasiten). */
  projects: DeviationProject[];
  /** Egne tildelte vs. loggede timer per ansatt per ferdigstilt prosjekt. */
  employeeHours: EmployeeProjectHours[];
  /** Aktive brukere (navn/rolle for ledelse-radene). */
  users: DeviationUser[];
  /** Assignment-grunnlag for oppgavebasert avvik, inkludert aktive prosjekter. */
  approvedTaskAssignments: ApprovedTaskAssignment[];
  /** FK-grunnlag: assignment først, entydig bruker+budsjettlinje som legacy fallback. */
  approvedTaskTimeEntries: ApprovedTaskTimeEntry[];
}

/**
 * Datagrunnlag for Feilbudsjettering-widgetene (VIB-1319). Henter ALT én gang
 * (hele året og mer); periodefiltrering (måned/kvartal/år) skjer klient-side
 * via pure kalkyler i src/lib/budget-deviation-calc.ts.
 *
 * Team-basis matcher use-bonus-goals.ts eksakt: solgte timer fra
 * budget_lines.quantity_hours (interne linjer), brukte timer fra alle
 * time_entries på prosjektet. Alle team-brede tabeller hentes paginert
 * (Supabase kutter stille ved 1000 rader).
 */
export function useBudgetDeviations() {
  return useQuery({
    queryKey: ['budget-deviations'],
    queryFn: async (): Promise<BudgetDeviationsData> => {
      const [rawProjects, budgetLines, timeEntries, assignments, rawUsers] = await Promise.all([
        fetchAllPaginated<RawProject>(
          'projects',
          'id, name, project_code, project_status, completed_at, customer:customer_id(name)',
          (q) => q.in('project_status', ['completed', 'active']).is('deleted_at', null),
        ),
        fetchAllPaginated<RawBudgetLine>(
          'budget_lines',
          'project_id, quantity_hours',
          (q) => q.eq('cost_type', 'internal'),
        ),
        fetchAllPaginated<RawTimeEntry>(
          'time_entries',
          'project_id, user_id, budget_line_id, assignment_id, duration_seconds',
          (q) => q.is('deleted_at', null),
        ),
        fetchAllPaginated<RawAssignment>(
          'budget_line_assignments',
          'id, user_id, budget_line_id, hours, label, task_status, completed_at, approved_at, budget_lines!inner(project_id, cost_type, description)',
          (q) => q.neq('budget_lines.cost_type', 'external'),
        ),
        fetchAllPaginated<RawUser>(
          'users',
          'id, full_name, role',
          (q) => q.eq('is_active', true),
        ),
      ]);

      const completedProjects = rawProjects.filter(
        (project) => project.project_status === 'completed' && project.completed_at != null,
      );
      const completedIds = new Set(completedProjects.map((project) => project.id));

      const soldByProject = new Map<string, number>();
      for (const bl of budgetLines) {
        if (!completedIds.has(bl.project_id)) continue;
        soldByProject.set(
          bl.project_id,
          (soldByProject.get(bl.project_id) ?? 0) + Number(bl.quantity_hours ?? 0),
        );
      }

      const usedByProject = new Map<string, number>();
      const loggedByUserProject = new Map<string, number>();
      for (const te of timeEntries) {
        if (!te.project_id || !completedIds.has(te.project_id)) continue;
        const hours = (te.duration_seconds ?? 0) / 3600;
        usedByProject.set(te.project_id, (usedByProject.get(te.project_id) ?? 0) + hours);
        if (te.user_id) {
          const key = `${te.user_id}|${te.project_id}`;
          loggedByUserProject.set(key, (loggedByUserProject.get(key) ?? 0) + hours);
        }
      }

      const assignedByUserProject = new Map<string, number>();
      for (const a of assignments) {
        const projectId = (a.budget_lines as { project_id: string } | null)?.project_id;
        if (!projectId || !completedIds.has(projectId)) continue;
        const key = `${a.user_id}|${projectId}`;
        assignedByUserProject.set(
          key,
          (assignedByUserProject.get(key) ?? 0) + Number(a.hours ?? 0),
        );
      }

      const projects: DeviationProject[] = completedProjects.map((p) => ({
        id: p.id,
        name: p.name,
        projectCode: p.project_code,
        customerName: (p.customer as { name: string } | null)?.name ?? null,
        completedAt: p.completed_at as string,
        teamSoldHours: soldByProject.get(p.id) ?? 0,
        teamUsedHours: usedByProject.get(p.id) ?? 0,
      }));

      // Slå sammen tildelt + logget per (bruker, prosjekt) til én radliste.
      const pairKeys = new Set([...assignedByUserProject.keys(), ...loggedByUserProject.keys()]);
      const employeeHours: EmployeeProjectHours[] = [];
      for (const key of pairKeys) {
        const [userId, projectId] = key.split('|');
        employeeHours.push({
          userId,
          projectId,
          assignedHours: assignedByUserProject.get(key) ?? 0,
          loggedHours: loggedByUserProject.get(key) ?? 0,
        });
      }

      const users: DeviationUser[] = rawUsers.map((u) => ({
        userId: u.id,
        fullName: u.full_name,
        role: u.role,
      }));

      const projectById = new Map(rawProjects.map((project) => [project.id, project]));
      const approvedTaskAssignments: ApprovedTaskAssignment[] = assignments.flatMap(
        (assignment) => {
          const budgetLine = assignment.budget_lines;
          if (!budgetLine) return [];
          const project = projectById.get(budgetLine.project_id);
          if (!project) return [];
          return [{
            assignmentId: assignment.id,
            userId: assignment.user_id,
            budgetLineId: assignment.budget_line_id,
            projectId: project.id,
            projectName: project.name,
            projectCode: project.project_code,
            customerName: project.customer?.name ?? null,
            taskName: assignment.label ?? budgetLine.description,
            taskStatus: assignment.task_status,
            completedAt: assignment.completed_at,
            approvedAt: assignment.approved_at,
            assignedHours: Number(assignment.hours ?? 0),
          }];
        },
      );
      const approvedTaskTimeEntries: ApprovedTaskTimeEntry[] = timeEntries.map((entry) => ({
        assignmentId: entry.assignment_id,
        userId: entry.user_id,
        budgetLineId: entry.budget_line_id,
        durationSeconds: entry.duration_seconds,
      }));

      return {
        projects,
        employeeHours,
        users,
        approvedTaskAssignments,
        approvedTaskTimeEntries,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}
